/**
 * KANBAN BOARD — Colony Design
 *
 * Linear-style pipeline visualization with Colony visual system.
 * Drag-and-drop task management with GitHub issue sync.
 */

import { useState, useEffect, memo, useMemo } from "react";
import { useWorldStore, type Task, type PipelineStage, CLOSE_ISSUE_ON_DONE } from "@/stores/worldStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import {
  Loader2,
  Github,
  AlertCircle,
  Play,
  Square,
  RotateCcw,
  Filter,
  MoreHorizontal,
  Plus,
  LayoutGrid,
  List,
  GanttChart,
  Lock,
  Link2,
  ChevronDown,
  ChevronRight,
  GitMerge,
  PauseCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSelectionStore } from "@/stores/selectionStore";
import { NewTaskModal } from "@/features/factory/NewTaskModal";
import { TaskDetailModal } from "./TaskDetailModal";
import { cn, truncateId } from "@/lib/utils";
import { useIsActiveStep } from "@/stores/onboardingStore";
import { StatusIcon, type TaskStatus } from "@/components/colony/StatusIcon";
import { AgentTypeAvatar, mapIssueToAgentType } from "@/components/colony/AgentTypeAvatar";

// ── Stage config ──────────────────────────────────────────────────────

const STAGES: {
  id: PipelineStage;
  label: string;
  status: TaskStatus;
}[] = [
  { id: "backlog",       label: "Backlog",       status: "backlog" },
  { id: "planning",      label: "Planning",      status: "todo" },
  { id: "in_progress",   label: "In Progress",   status: "in-progress" },
  { id: "ai_review",     label: "AI Review",     status: "in-review" },
  { id: "human_review",  label: "Human Review",  status: "in-review" },
  { id: "done",          label: "Done",          status: "done" },
  { id: "error",         label: "Error",         status: "error" },
];

const getLabelsForStage = (stage: PipelineStage): string[] => {
  switch (stage) {
    case "backlog":
      return ["factory:stage/backlog", "factory:status/pending"];
    case "planning":
      return ["factory:stage/planning", "factory:status/pending"];
    case "in_progress":
      return ["factory:stage/in-progress", "factory:status/in-progress"];
    case "ai_review":
    case "human_review":
      return ["factory:stage/review", "factory:status/review"];
    case "done":
      return ["factory:stage/done", "factory:status/done"];
    case "error":
      return ["factory:stage/error", "factory:status/error"];
    default:
      return ["factory:stage/backlog", "factory:status/pending"];
  }
};

// ── Small UI pieces ──────────────────────────────────────────────────

function TagBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center h-[18px] px-1.5 rounded-[3px] bg-[#2A2A2F] border border-[rgba(255,255,255,0.06)] text-[10px] text-colony-text-secondary whitespace-nowrap">
      {label}
    </span>
  );
}

function PrBadge({ pr }: { pr: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 h-[18px] px-1.5 rounded-[3px] bg-[#2A2A2F] border border-[rgba(255,255,255,0.06)] text-[10px] font-mono text-colony-text-tertiary whitespace-nowrap">
      <svg width="10" height="10" viewBox="0 0 16 16" fill="none" className="shrink-0 opacity-60">
        <path d="M5 3.5V12.5M5 3.5C5 4.88 3.88 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 5 2.12 5 3.5ZM5 12.5C5 13.88 3.88 15 2.5 15S0 13.88 0 12.5 1.12 10 2.5 10 5 11.12 5 12.5ZM16 3.5C16 4.88 14.88 6 13.5 6S11 4.88 11 3.5 12.12 1 13.5 1 16 2.12 16 3.5ZM5 3.5H11M11 3.5V8C11 10.2 9.2 12 7 12H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {pr}
    </span>
  );
}

function AgentActivity({ status }: { status: "working" | "finished" | "idle" }) {
  if (status === "working") {
    return (
      <span
        className="text-[11px] text-[#00B2C8] whitespace-nowrap"
        style={{
          background: "linear-gradient(90deg, #00B2C8 0%, #00d4ea 50%, #00B2C8 100%) 0 0 / 200% 100% no-repeat",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          animation: "kanbanShimmer 3s linear infinite",
        }}
      >
        Working...
      </span>
    );
  }
  if (status === "finished") {
    return <span className="text-[11px] text-colony-green whitespace-nowrap">Finished</span>;
  }
  return null;
}

type BoardViewMode = "board" | "list" | "timeline";

function ViewToggle({ active, onChange }: { active: BoardViewMode; onChange: (v: BoardViewMode) => void }) {
  const items: { id: BoardViewMode; icon: React.ReactNode; label: string }[] = [
    { id: "board", icon: <LayoutGrid size={13} strokeWidth={1.5} />, label: "Board" },
    { id: "list", icon: <List size={13} strokeWidth={1.5} />, label: "List" },
    { id: "timeline", icon: <GanttChart size={13} strokeWidth={1.5} />, label: "Timeline" },
  ];

  return (
    <div className="flex items-center h-7 rounded-[6px] bg-[#151518] border border-[rgba(255,255,255,0.06)] p-0.5 gap-0.5">
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => onChange(item.id)}
          className={`flex items-center gap-1.5 h-6 px-2.5 rounded-[4px] text-[11px] cursor-pointer transition-all duration-150 ${
            active === item.id
              ? "bg-[#2A2A2F] text-colony-text-primary shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
              : "text-colony-text-tertiary hover:text-colony-text-secondary"
          }`}
          style={{ transitionTimingFunction: "var(--ease-out-expo)" }}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────

function ListView({
  tasks,
  onTaskClick,
  syncingTaskId,
}: {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  syncingTaskId: string | null;
}) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const groups = useMemo(
    () =>
      STAGES.map((stage) => ({
        stage,
        tasks: tasks.filter((t) => t.pipelineStage === stage.id),
      })).filter((g) => g.tasks.length > 0),
    [tasks],
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Column headers */}
      <div className="flex items-center h-8 px-2 border-b border-[rgba(255,255,255,0.06)] shrink-0 bg-[rgba(255,255,255,0.02)]">
        <div className="w-8 shrink-0" />
        <div className="w-[72px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">ID</div>
        <div className="flex-1 min-w-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Title</div>
        <div className="w-[100px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Type</div>
        <div className="w-[80px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Pipeline</div>
        <div className="w-[40px] shrink-0" />
      </div>

      {/* Grouped rows */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
        {groups.map(({ stage, tasks: stageTasks }) => (
          <div key={stage.id}>
            {/* Group header */}
            <button
              onClick={() => toggleGroup(stage.id)}
              className="flex items-center gap-2 w-full h-8 px-3 bg-[#111114] border-b border-[rgba(255,255,255,0.04)] cursor-pointer hover:bg-[#151518] transition-colors"
            >
              {collapsedGroups.has(stage.id) ? (
                <ChevronRight size={12} strokeWidth={2} className="text-colony-text-tertiary" />
              ) : (
                <ChevronDown size={12} strokeWidth={2} className="text-colony-text-tertiary" />
              )}
              <StatusIcon status={stage.status} size={14} />
              <span className="text-[11px] font-medium text-colony-text-secondary">
                {stage.label}
              </span>
              <span className="text-[10px] text-colony-text-tertiary font-mono tabular-nums">
                {stageTasks.length}
              </span>
            </button>

            {/* Task rows */}
            {!collapsedGroups.has(stage.id) &&
              stageTasks.map((task) => {
                const issueNumber = task.githubIssueUrl?.match(/\/issues\/(\d+)/)?.[1];
                const agentType = mapIssueToAgentType(task.issueType || "feature");
                const isPipelineActive = task.workflowStatus === "running" || task.workflowStatus === "queued";
                const isPipelineFailed = task.workflowStatus === "failed";
                const isPipelinePaused = task.workflowStatus === "paused";
                const steps = getStepsFromMetadata(task.metadata);
                const isSyncing = syncingTaskId === task.id;

                return (
                  <div
                    key={task.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onTaskClick(task)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTaskClick(task); } }}
                    aria-label={`Open task: ${task.title}`}
                    className={cn(
                      "flex items-center h-10 px-2 border-b border-[rgba(255,255,255,0.06)] cursor-pointer transition-colors duration-75",
                      isSyncing
                        ? "opacity-50 pointer-events-none"
                        : "hover:bg-[rgba(255,255,255,0.03)]"
                    )}
                  >
                    {/* Status icon */}
                    <div className="w-8 flex items-center justify-center shrink-0">
                      <StatusIcon status={stage.status} size={16} />
                    </div>

                    {/* Issue ID */}
                    <div className="w-[72px] shrink-0">
                      <span className="text-[11px] font-mono text-colony-text-secondary whitespace-nowrap" style={{ letterSpacing: "0.01em" }}>
                        {issueNumber ? `#${issueNumber}` : truncateId(task.id)}
                      </span>
                    </div>

                    {/* Title */}
                    <div className="flex-1 min-w-0 pr-3">
                      <span className="text-[13px] text-colony-text-primary truncate block">
                        {task.title}
                      </span>
                    </div>

                    {/* Agent type */}
                    <div className="w-[100px] shrink-0 flex items-center gap-1.5">
                      <AgentTypeAvatar type={agentType} size={16} active={isPipelineActive} />
                      <span className="text-[11px] text-colony-text-tertiary truncate capitalize">
                        {task.issueType === "wp" ? "Refactor" : task.issueType || "feature"}
                      </span>
                    </div>

                    {/* Pipeline step dots */}
                    <div className="w-[80px] shrink-0 flex items-center gap-0.5">
                      {steps && steps.length > 0 ? (
                        steps.map((step) => (
                          <div
                            key={step.key}
                            title={step.key}
                            className={cn("w-2 h-2 rounded-full", step.status === "running" && "animate-pulse")}
                            style={{
                              backgroundColor:
                                step.status === "completed" ? "var(--colony-green)"
                                : step.status === "running" ? "var(--colony-violet)"
                                : step.status === "failed" ? "var(--colony-red)"
                                : "#2A2A2F",
                            }}
                          />
                        ))
                      ) : isPipelineActive ? (
                        <Loader2 className="w-3 h-3 animate-spin text-[#00B2C8]" />
                      ) : isPipelinePaused ? (
                        <PauseCircle className="w-3 h-3 text-amber-400" />
                      ) : isPipelineFailed ? (
                        <AlertCircle className="w-3 h-3 text-colony-red" />
                      ) : null}
                    </div>

                    {/* GitHub link */}
                    <div className="w-[40px] shrink-0 flex items-center justify-center">
                      {issueNumber && (
                        <a
                          href={task.githubIssueUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-colony-text-tertiary hover:text-colony-text-secondary transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Github size={14} strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ))}
        {tasks.length === 0 && (
          <div className="flex items-center justify-center h-32">
            <p className="text-[12px] text-colony-text-tertiary">No tasks yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Timeline View ─────────────────────────────────────────────────────

function TimelineView({
  tasks,
  onTaskClick,
}: {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
}) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Stage column headers */}
      <div className="flex h-8 border-b border-[rgba(255,255,255,0.06)] shrink-0">
        <div className="w-[220px] shrink-0 px-3 flex items-center">
          <span className="text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Task</span>
        </div>
        <div className="flex-1 flex">
          {STAGES.map((stage, idx) => (
            <div
              key={stage.id}
              className={cn(
                "flex-1 flex items-center justify-center gap-1",
                idx > 0 && "border-l border-[rgba(255,255,255,0.04)]"
              )}
            >
              <StatusIcon status={stage.status} size={11} />
              <span className="text-[10px] text-colony-text-tertiary whitespace-nowrap">{stage.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Task rows */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
        {tasks.map((task) => {
          const stageIdx = STAGES.findIndex((s) => s.id === task.pipelineStage);
          const isPipelineActive = task.workflowStatus === "running" || task.workflowStatus === "queued" || task.workflowStatus === "paused";
          const isDone = task.pipelineStage === "done";
          const isError = task.pipelineStage === "error";
          const issueNumber = task.githubIssueUrl?.match(/\/issues\/(\d+)/)?.[1];
          const agentType = mapIssueToAgentType(task.issueType || "feature");

          const barColor = isDone
            ? "var(--colony-green)"
            : isError
            ? "var(--colony-red)"
            : isPipelineActive
            ? "#00B2C8"
            : "#555560";

          // Bar spans from stage 0 to current stage (inclusive)
          const barEndPct = ((stageIdx + 1) / STAGES.length) * 100;

          return (
            <div
              key={task.id}
              role="button"
              tabIndex={0}
              onClick={() => onTaskClick(task)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTaskClick(task); } }}
              aria-label={`Open task: ${task.title}`}
              className="flex h-11 border-b border-[rgba(255,255,255,0.06)] cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
            >
              {/* Task info */}
              <div className="w-[220px] shrink-0 px-3 flex items-center gap-2 min-w-0">
                <AgentTypeAvatar type={agentType} size={16} active={isPipelineActive} />
                <span className="text-[11px] font-mono text-colony-text-tertiary shrink-0">
                  {issueNumber ? `#${issueNumber}` : task.id.slice(0, 6)}
                </span>
                <span className="text-[12px] text-colony-text-primary truncate">{task.title}</span>
              </div>

              {/* Timeline bar area */}
              <div className="flex-1 flex relative">
                {/* Grid lines */}
                {STAGES.map((stage, idx) => (
                  <div
                    key={stage.id}
                    className={cn("flex-1", idx > 0 && "border-l border-[rgba(255,255,255,0.04)]")}
                  />
                ))}

                {/* Bar overlay */}
                <div className="absolute inset-y-0 left-0 right-0 flex items-center pointer-events-none">
                  <div className="relative w-full h-[6px] mx-2">
                    {/* Background track */}
                    <div className="absolute inset-0 rounded-full bg-[#1A1A1F]" />
                    {/* Filled bar */}
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                        isPipelineActive && "timeline-bar-pulse"
                      )}
                      style={{
                        width: `${barEndPct}%`,
                        backgroundColor: barColor,
                        opacity: isDone || isError ? 0.8 : 0.5,
                        boxShadow: isPipelineActive ? `0 0 8px ${barColor}40` : undefined,
                      }}
                    />
                    {/* Current position dot */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-[9px] h-[9px] rounded-full border-[1.5px] border-[#1C1C1F]"
                      style={{
                        left: `calc(${barEndPct}% - 5px)`,
                        backgroundColor: barColor,
                        boxShadow: isPipelineActive ? `0 0 6px ${barColor}80` : undefined,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div className="flex items-center justify-center h-32">
            <p className="text-[12px] text-colony-text-tertiary">No tasks yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Board ────────────────────────────────────────────────────────

interface KanbanBoardProps {
  projectId?: number;
}

export function KanbanBoard({ projectId }: KanbanBoardProps) {
  const projects = useWorldStore((s) => s.projects);
  const tasks = useWorldStore((s) => s.tasks);
  const syncingTaskId = useWorldStore((s) => s.syncingTaskId);
  const moveTaskToStage = useWorldStore((s) => s.moveTaskToStage);
  const updateTaskWithGitHubSync = useWorldStore((s) => s.updateTaskWithGitHubSync);
  const startTask = useWorldStore((s) => s.startTask);
  const stopTask = useWorldStore((s) => s.stopTask);
  const retryTaskPipeline = useWorldStore((s) => s.retryTaskPipeline);
  const { getLabelsForStage: getWorkflowLabels } = useWorkflowStore();
  const { toast } = useToast();
  const { activeProjectForKanban, selectedType, selectedId } = useSelectionStore();

  const [showNewTask, setShowNewTask] = useState(false);
  const [dragOverStage, setDragOverStage] = useState<PipelineStage | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<BoardViewMode>("board");

  const fallbackSelectedProjectId =
    selectedType === "project" && typeof selectedId === "number"
      ? selectedId
      : null;
  const resolvedProjectId = projectId ?? activeProjectForKanban ?? fallbackSelectedProjectId ?? null;

  const project = useMemo(
    () => resolvedProjectId != null ? projects.find((p) => Number(p.id) === Number(resolvedProjectId)) : undefined,
    [projects, resolvedProjectId],
  );
  const projectTasks = useMemo(
    () => resolvedProjectId != null ? tasks.filter((t) => Number(t.projectId) === Number(resolvedProjectId)) : [],
    [tasks, resolvedProjectId],
  );

  const projectName = project?.name;
  const projectsList = project ? [{ id: Number(project.id), name: project.name }] : [];

  if (resolvedProjectId == null) {
    return (
      <div className="flex items-center justify-center flex-1">
        <p className="text-[13px] text-colony-text-tertiary">No project selected</p>
      </div>
    );
  }

  // ── Drag & drop ───────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData("taskId", taskId);
    e.dataTransfer.effectAllowed = "move";
    document.body.classList.add("is-dragging");
  };

  const handleDragEnd = () => {
    setDragOverStage(null);
    document.body.classList.remove("is-dragging");
  };

  const handleDragOver = (e: React.DragEvent, stageId: PipelineStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, stage: PipelineStage) => {
    e.preventDefault();
    setDragOverStage(null);
    document.body.classList.remove("is-dragging");
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.pipelineStage === stage) return;

    // Guard: block drag while workflow is active or paused
    if (task.workflowStatus === "running" || task.workflowStatus === "queued" || task.workflowStatus === "paused") {
      toast({
        title: "Cannot move task",
        description: task.workflowStatus === "paused"
          ? "Task is paused — use Approve/Reject/Stop instead of dragging."
          : "Task has an active workflow — stop it first.",
        variant: "destructive",
      });
      return;
    }

    if (task.githubIssueUrl) {
      const workflowLabels = getWorkflowLabels(stage, task.workflowId);
      const labels = workflowLabels?.labels || getLabelsForStage(stage);
      const typeLabel =
        task.issueType === "bug"
          ? "factory:type/bug"
          : task.issueType === "wp"
          ? "factory:type/refactor"
          : "factory:type/feature";
      const mergedLabels = Array.from(new Set([...(labels || []), typeLabel]));
      const issueState = stage === "done" && CLOSE_ISSUE_ON_DONE ? "closed" : undefined;
      try {
        await updateTaskWithGitHubSync(taskId, stage, mergedLabels, issueState);
      } catch (error) {
        toast({
          title: "Sync failed",
          description: error instanceof Error ? error.message : "Could not update GitHub issue",
          variant: "destructive",
        });
      }
    } else {
      moveTaskToStage(taskId, stage);
    }
  };

  // ── Actions ───────────────────────────────────────────────────────

  const handleStartTask = async (taskId: string) => {
    const success = await startTask(taskId);
    if (!success) {
      const reason = useWorldStore.getState().saveError;
      toast({
        title: "Start failed",
        description: reason || "Failed to start task.",
        variant: "destructive",
      });
    }
    return success;
  };

  const handleStopTask = async (taskId: string) => {
    const success = await stopTask(taskId);
    if (!success) {
      const reason = useWorldStore.getState().saveError;
      toast({
        title: "Stop failed",
        description: reason || "Failed to stop task.",
        variant: "destructive",
      });
    }
    return success;
  };

  const handleRetryTask = async (taskId: string) => {
    const success = await retryTaskPipeline(taskId);
    if (!success) {
      const reason = useWorldStore.getState().saveError;
      toast({
        title: "Retry failed",
        description: reason || "Failed to retry task.",
        variant: "destructive",
      });
    }
    return success;
  };

  // ── Render ────────────────────────────────────────────────────────

  const totalTasks = projectTasks.length;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ color: "var(--colony-text-primary)" }}>
      {/* ── Page Header ─── */}
      <div className="flex items-center justify-between px-5 h-12 shrink-0 border-b border-[rgba(255,255,255,0.06)]">
        <div className="flex items-center gap-2">
          <h2 className="text-[16px] text-colony-text-heading tracking-[-0.015em]">
            Agent tasks
          </h2>
          <button aria-label="Task list options" className="text-colony-text-tertiary hover:text-colony-text-secondary transition-colors cursor-pointer">
            <MoreHorizontal size={14} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] text-[12px] text-colony-text-secondary hover:text-colony-text-primary hover:bg-colony-surface-elevated transition-all duration-150 cursor-pointer">
            <Filter size={13} strokeWidth={1.5} />
            Filter
          </button>

          <div className="w-px h-4 bg-colony-border mx-0.5" />

          <ViewToggle active={viewMode} onChange={setViewMode} />

          <div className="w-px h-4 bg-colony-border mx-0.5" />

          <button
            onClick={() => setShowNewTask(true)}
            className="group/newtask flex items-center gap-1.5 h-7 px-3 rounded-[6px] text-[12px] font-medium text-white cursor-pointer transition-all duration-200"
            style={{
              background: "var(--colony-brand)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.3)",
              transitionTimingFunction: "var(--ease-out-expo)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--colony-brand-hover)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.15), 0 2px 6px rgba(0,0,0,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--colony-brand)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.3)";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "scale(0.97) translateY(0)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            data-testid="button-new-task"
          >
            <Plus size={14} strokeWidth={2} />
            New Task
          </button>
        </div>
      </div>

      {/* ── Issue count sub-bar ─── */}
      <div className="flex items-center px-5 h-8 shrink-0">
        <span className="text-[12px] text-colony-text-tertiary tabular-nums">
          {totalTasks} issue{totalTasks !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Content area ─── */}
      {viewMode === "board" && (
        <div className="flex-1 overflow-x-auto overflow-y-auto px-5 pb-6">
          <div className="flex gap-5 min-w-max">
            {STAGES.map((stage) => {
              const stageTasks = projectTasks.filter((t) => t.pipelineStage === stage.id);
              const isDragOver = dragOverStage === stage.id;

              return (
                <div
                  key={stage.id}
                  className={cn(
                    "flex flex-col min-w-[220px] w-[220px] shrink-0 transition-colors duration-150 rounded-[6px]",
                    isDragOver && "bg-[rgba(0,178,200,0.04)]"
                  )}
                  onDragOver={(e) => handleDragOver(e, stage.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  data-testid={`column-${stage.id}`}
                >
                  {/* Column Header */}
                  <div className="group/col flex items-center gap-2 px-1 h-8 mb-2">
                    <StatusIcon status={stage.status} size={14} />
                    <span className="text-[13px] text-colony-text-primary whitespace-nowrap">
                      {stage.label}
                    </span>
                    <span className="text-[13px] text-colony-text-tertiary tabular-nums">
                      {stageTasks.length}
                    </span>
                    <div className="flex-1" />
                    <div className="flex items-center gap-0.5 opacity-0 group-hover/col:opacity-100 transition-opacity duration-150">
                      <button aria-label={`Add task to ${stage.label}`} className="flex items-center justify-center w-5 h-5 rounded-[4px] text-[#6E6E7A] hover:text-[#B0B0B8] hover:bg-[rgba(255,255,255,0.06)] transition-all duration-150 cursor-pointer">
                        <Plus size={14} strokeWidth={1.5} />
                      </button>
                      <button aria-label={`${stage.label} column options`} className="flex items-center justify-center w-5 h-5 rounded-[4px] text-[#6E6E7A] hover:text-[#B0B0B8] hover:bg-[rgba(255,255,255,0.06)] transition-all duration-150 cursor-pointer">
                        <MoreHorizontal size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  {/* Cards */}
                  <div className="flex flex-col gap-1.5 flex-1 min-h-[60px]">
                    {stageTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        isSyncing={syncingTaskId === task.id}
                        onClick={() => setSelectedTask(task)}
                        onStart={handleStartTask}
                        onStop={handleStopTask}
                        onRetry={handleRetryTask}
                      />
                    ))}
                    {stageTasks.length === 0 && stage.id === "backlog" && (
                      <div className="text-center py-8 px-3">
                        <p className="text-[12px] text-colony-text-tertiary mb-1">Your backlog is empty</p>
                        <p className="text-[10px] text-colony-text-tertiary">
                          Create a new task to get started
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === "list" && (
        <ListView
          tasks={projectTasks}
          onTaskClick={(t) => setSelectedTask(t)}
          syncingTaskId={syncingTaskId}
        />
      )}

      {viewMode === "timeline" && (
        <TimelineView
          tasks={projectTasks}
          onTaskClick={(t) => setSelectedTask(t)}
        />
      )}

      {/* ── Modals ─── */}
      <NewTaskModal
        isOpen={showNewTask}
        onClose={() => setShowNewTask(false)}
        defaultProjectId={resolvedProjectId != null ? Number(resolvedProjectId) : null}
      />
      <TaskDetailModal
        task={selectedTask}
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  );
}

// ── Step progress helpers ────────────────────────────────────────────

interface StepInfo {
  key: string;
  status: string;
}

function getStepsFromMetadata(metadata?: Record<string, any>): StepInfo[] | null {
  if (!metadata) return null;
  const source = metadata.bugfix_steps || metadata.workflow_steps;
  if (!source || typeof source !== "object" || Object.keys(source).length === 0) return null;
  return Object.entries(source).map(([key, value]: [string, any]) => ({
    key,
    status: value?.status || "pending",
  }));
}

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}

function useElapsed(active: boolean, startTime?: string): string | null {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active]);
  if (!active || !startTime) return null;
  const elapsed = now - new Date(startTime).getTime();
  return elapsed > 0 ? formatElapsed(elapsed) : null;
}

function getWorkflowStartTime(metadata?: Record<string, any>): string | undefined {
  if (!metadata) return undefined;
  const source = metadata.bugfix_steps || metadata.workflow_steps;
  if (!source) return undefined;
  let earliest: string | undefined;
  for (const step of Object.values(source) as any[]) {
    const t = step?.started_at || step?.completed_at;
    if (t && (!earliest || t < earliest)) earliest = t;
  }
  return earliest;
}

function getWorkflowLastOutput(metadata?: Record<string, any>): string | undefined {
  if (!metadata) return undefined;
  const direct = typeof metadata.workflowLastOutput === "string" ? metadata.workflowLastOutput.trim() : "";
  if (direct) return direct;
  const tail = Array.isArray(metadata.workflowOutputTail) ? metadata.workflowOutputTail : [];
  for (let idx = tail.length - 1; idx >= 0; idx -= 1) {
    const entry = tail[idx];
    if (typeof entry === "string" && entry.trim().length > 0) return entry.trim();
  }
  return undefined;
}

const STEP_LABELS: Record<string, string> = {
  diagnose: "Diagnosing",
  fix: "Fixing",
  verify: "Verifying",
  review: "Reviewing",
  pr: "Creating PR",
};

// ── Task Card — Colony Design ────────────────────────────────────────

const TaskCard = memo(function TaskCard({
  task,
  onDragStart,
  onDragEnd,
  isSyncing = false,
  onClick,
  onStart,
  onStop,
  onRetry,
}: {
  task: Task;
  onDragStart: (e: React.DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  isSyncing?: boolean;
  onClick: () => void;
  onStart?: (taskId: string) => Promise<boolean>;
  onStop?: (taskId: string) => Promise<boolean>;
  onRetry?: (taskId: string) => Promise<boolean>;
}) {
  const { resolveWorkflowForTask } = useWorkflowStore();
  const pulseStart = useIsActiveStep("run_agent");
  const agentType = mapIssueToAgentType(task.issueType || "feature");
  const workflow = resolveWorkflowForTask(task);

  const issueNumber = task.githubIssueUrl?.match(/\/issues\/(\d+)/)?.[1];
  const isPipelineActive = task.workflowStatus === "running" || task.workflowStatus === "queued";
  const isPipelineFailed = task.workflowStatus === "failed";
  const isPipelinePaused = task.workflowStatus === "paused";
  const canStart = onStart && (task.pipelineStage === "backlog" || task.pipelineStage === "planning") && !isPipelineActive;
  const currentStep = task.metadata?.current_step;
  const stepLabel = currentStep ? STEP_LABELS[currentStep] || currentStep : null;
  const workflowLastOutput = getWorkflowLastOutput(task.metadata);
  const steps = getStepsFromMetadata(task.metadata);
  const startTime = getWorkflowStartTime(task.metadata);
  const elapsed = useElapsed(isPipelineActive, startTime);

  const activityStatus: "working" | "finished" | "idle" =
    isPipelineActive ? "working"
    : task.pipelineStage === "done" ? "finished"
    : "idle";

  return (
    <div
      draggable={!isPipelineActive}
      onDragStart={(e) => !isPipelineActive && onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={cn(
        "group/card relative rounded-[6px] border cursor-grab active:cursor-grabbing transition-all duration-150",
        isSyncing && "opacity-60 pointer-events-none",
        isPipelineActive && "cursor-default",
        isPipelineFailed
          ? "border-[hsla(0,70%,50%,0.2)]"
          : isPipelinePaused
          ? "border-[hsla(38,92%,50%,0.25)]"
          : "border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.10)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.3),0_1px_2px_rgba(0,0,0,0.2)]",
      )}
      style={{
        background: "var(--gradient-card)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.1)",
        transitionTimingFunction: "var(--ease-out-expo)",
      }}
      data-testid={`card-task-${task.id}`}
    >
      {/* Working shimmer border overlay */}
      {isPipelineActive && (
        <div
          className="absolute inset-0 rounded-[6px] pointer-events-none"
          style={{
            border: "1px solid transparent",
            backgroundImage:
              "linear-gradient(var(--colony-surface), var(--colony-surface)), linear-gradient(90deg, transparent 0%, rgba(0,178,200,0.3) 50%, transparent 100%)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            backgroundSize: "100% 100%, 200% 100%",
            animation: "kanbanShimmer 3s linear infinite",
          }}
        />
      )}

      <div className="relative px-3 py-2.5">
        {/* Row 1: Issue ID + Activity + Agent Avatar */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-mono text-colony-text-secondary whitespace-nowrap" style={{ letterSpacing: "0.01em" }}>
            {issueNumber ? `#${issueNumber}` : truncateId(task.id)}
          </span>
          <div className="flex items-center gap-1.5">
            <AgentActivity status={activityStatus} />
            <AgentTypeAvatar type={agentType} size={18} active={isPipelineActive} />
          </div>
        </div>

        {/* Row 2: Title */}
        <p className="text-[13px] text-colony-text-primary leading-[1.35] line-clamp-2 mb-2">
          {task.title}
        </p>

        {/* Row 3: Tags + PR + GitHub + Merge indicator */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {workflow && <TagBadge label={workflow.name} />}
          {task.issueType && <TagBadge label={task.issueType === "wp" ? "Refactor" : task.issueType} />}
          {issueNumber && (
            <a
              href={task.githubIssueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 h-[18px] px-1.5 rounded-[3px] bg-[#2A2A2F] border border-[rgba(255,255,255,0.06)] text-[10px] font-mono text-colony-text-tertiary whitespace-nowrap hover:text-colony-text-secondary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Github size={10} className="shrink-0 opacity-60" />
              #{issueNumber}
            </a>
          )}
          {task.metadata?.pr_url && (task.pipelineStage === "ai_review" || task.pipelineStage === "human_review" || task.pipelineStage === "done") && (
            <span
              className="inline-flex items-center gap-0.5 h-[18px] px-1.5 rounded-[3px] border text-[10px] font-medium whitespace-nowrap"
              style={{
                borderColor: "rgba(1, 177, 157, 0.25)",
                backgroundColor: "rgba(1, 177, 157, 0.1)",
                color: "#01B19D",
              }}
              title="PR ready to merge"
            >
              <GitMerge size={10} className="shrink-0" />
              PR
            </span>
          )}
        </div>

        {/* Pipeline status — step progress */}
        {isPipelineActive && (
          <div className="mt-2 space-y-1.5">
            {steps && steps.length > 0 && (
              <div className="flex items-center gap-1">
                {steps.map((step) => (
                  <div
                    key={step.key}
                    title={step.key}
                    className={cn("w-2 h-2 rounded-full transition-colors", step.status === "running" && "animate-pulse")}
                    style={{
                      backgroundColor: step.status === "completed" ? "var(--colony-green)"
                        : step.status === "running" ? "var(--colony-violet)"
                        : step.status === "failed" ? "var(--colony-red)"
                        : "var(--colony-text-tertiary)",
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin text-[#00B2C8] flex-shrink-0" />
              <span className="text-[11px] text-[#00B2C8] truncate">
                {task.workflowStatus === "queued" ? "Queued..." : stepLabel ? `${stepLabel}...` : "Running..."}
              </span>
              {elapsed && (
                <span className="text-[11px] text-colony-text-tertiary tabular-nums ml-auto flex-shrink-0">
                  {elapsed}
                </span>
              )}
            </div>
            {workflowLastOutput && (
              <p className="text-[10px] text-colony-text-tertiary line-clamp-2 break-words">
                {workflowLastOutput}
              </p>
            )}
          </div>
        )}

        {/* Pipeline failed */}
        {isPipelineFailed && (
          <div className="mt-2 space-y-1.5">
            {steps && steps.length > 0 && (
              <div className="flex items-center gap-1">
                {steps.map((step) => (
                  <div
                    key={step.key}
                    title={step.key}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: step.status === "completed" ? "var(--colony-green)"
                        : step.status === "failed" ? "var(--colony-red)"
                        : "var(--colony-text-tertiary)",
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3 text-colony-red flex-shrink-0" />
              <span className="text-[11px] text-colony-red flex-1">
                Failed{currentStep ? ` at ${currentStep}` : ""}
              </span>
              {onRetry && (
                <button
                  className="flex items-center gap-1 h-5 px-2 rounded-[4px] text-[10px] font-medium text-colony-text-secondary bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.1)] hover:text-colony-text-primary transition-all duration-150 cursor-pointer"
                  title="Retry task (clean slate)"
                  onClick={(e) => { e.stopPropagation(); onRetry(task.id); }}
                >
                  <RotateCcw className="w-3 h-3" />
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        {/* Pipeline paused — needs input */}
        {isPipelinePaused && (
          <div className="mt-2 flex items-center gap-1.5">
            <PauseCircle className="w-3 h-3 text-amber-400 flex-shrink-0" />
            <span className="text-[11px] text-amber-400">
              Needs input
            </span>
          </div>
        )}

        {/* Blocked / blocking indicators */}
        {task.metadata?.blocked_by && (
          <div className="flex items-center gap-1 mt-1.5">
            <Lock size={9} strokeWidth={2} className="text-colony-red" />
            <span className="text-[10px] text-colony-red">Blocked by {task.metadata.blocked_by}</span>
          </div>
        )}
        {task.metadata?.blocks_count && task.metadata.blocks_count > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <Link2 size={9} strokeWidth={2} className="text-colony-amber" />
            <span className="text-[10px] text-colony-amber">Blocks {task.metadata.blocks_count} task{task.metadata.blocks_count > 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {/* Start button */}
      {canStart && onStart && (
        <button
          className={cn(
            "absolute top-2 right-2 transition-opacity p-1 rounded-[4px] bg-emerald-600 hover:bg-emerald-500 text-white",
            pulseStart ? "opacity-100 onboarding-pulse" : "opacity-0 group-hover/card:opacity-100"
          )}
          title="Start task"
          onClick={(e) => { e.stopPropagation(); onStart(task.id); }}
        >
          <Play className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Stop button */}
      {isPipelineActive && onStop && (
        <button
          className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity p-1 rounded-[4px] bg-red-600 hover:bg-red-500 text-white"
          title="Stop task"
          onClick={(e) => { e.stopPropagation(); onStop(task.id); }}
        >
          <Square className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Syncing overlay */}
      {isSyncing && (
        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(17,17,19,0.5)] rounded-[6px] backdrop-blur-sm">
          <Loader2 className="w-5 h-5 animate-spin text-[#00B2C8]" />
        </div>
      )}
    </div>
  );
});
